import { useMemo } from 'react';

import { Usage } from '../../../telemetry/gen/ai/h2o/cloud/telemetry_read_api/v1/aiunits_pb';
import {
  EnrichedWorkspace,
  ServiceFilterOption,
  TagFilterOption,
  User,
  UserFilterOption,
  WorkspaceFilterOption,
} from '../types';
import {
  extractServiceFilterOptions,
  extractTagFilterOptions,
  extractUserFilterOptions,
  extractWorkspaceFilterOptions,
} from '../utils/extractFilterOptions';

/**
 * Result from prefetched filter options hook
 */
export interface PrefetchedFilterOptionsResult {
  workspaceOptions: WorkspaceFilterOption[];
  userOptions: UserFilterOption[];
  tagOptions: TagFilterOption[];
  serviceOptions: ServiceFilterOption[];
}

/**
 * Custom hook to generate filter options from already-prefetched workspace, user, tag, and service data
 *
 * This hook extracts filter options from the workspace, user, tag, and service usage data that's
 * already being fetched by useMultiDimensionalPrefetch. This avoids duplicate API calls
 * and ensures filters are populated when the chart data loads.
 *
 * @param workspaceData Usage data grouped by workspace (from useMultiDimensionalPrefetch)
 * @param userData Usage data grouped by user (from useMultiDimensionalPrefetch)
 * @param tagData Usage data grouped by tag (from useMultiDimensionalPrefetch)
 * @param serviceData Usage data grouped by service (from useMultiDimensionalPrefetch)
 * @param workspacesMap Map of workspace IDs to enriched workspace objects
 * @param usersMap Map of user IDs to user objects
 * @returns Object containing workspace, user, tag, and service filter options
 */
export function usePrefetchedFilterOptions(
  workspaceData: Usage[] | undefined,
  userData: Usage[] | undefined,
  tagData: Usage[] | undefined,
  serviceData: Usage[] | undefined,
  workspacesMap: Map<string, EnrichedWorkspace>,
  usersMap: Map<string, User>
): PrefetchedFilterOptionsResult {
  // Generate workspace filter options from prefetched data
  const workspaceOptions = useMemo<WorkspaceFilterOption[]>(() => {
    const options = extractWorkspaceFilterOptions(workspaceData, workspacesMap);
    return options;
  }, [workspaceData, workspacesMap]);

  // Generate user filter options from prefetched data
  const userOptions = useMemo<UserFilterOption[]>(() => {
    const options = extractUserFilterOptions(userData, usersMap);
    return options;
  }, [userData, usersMap]);

  // Generate tag filter options from prefetched data
  const tagOptions = useMemo<TagFilterOption[]>(() => {
    const options = extractTagFilterOptions(tagData);
    return options;
  }, [tagData]);

  // Generate service filter options from prefetched data
  const serviceOptions = useMemo<ServiceFilterOption[]>(() => {
    const options = extractServiceFilterOptions(serviceData);
    return options;
  }, [serviceData]);

  return {
    workspaceOptions,
    userOptions,
    tagOptions,
    serviceOptions,
  };
}
